import { useAuth } from '@clerk/nextjs';
import { Unit } from '@interfaces/singlePublicBoat';
import { useSettings } from '@marine/services';
import { ltToT } from '@utils/UnitHandler';
import convert from 'convert-units';
import React from 'react';

type Props = {
  value: number;
  unit: Unit;
};

/**
 * Decimals shown per unit, defaulting to `DEFAULT_FRACTION_DIGITS`.
 *
 * Weights (`lb` — displacement and ballast weight) are rounded to whole numbers: the lb→kg
 * conversion turns a round stored figure into a long fraction (4,400 lb → 1,995.806 kg) that
 * carries no useful precision at that magnitude.
 */
const FRACTION_DIGITS_BY_UNIT: Partial<Record<Unit, number>> = {
  lb: 0,
};

const DEFAULT_FRACTION_DIGITS = 2;

const UnitDetail: React.FC<Props> = ({ value, unit }) => {
  // These pages are public: without a session the settings request can only answer 401, so a
  // visitor reads the metric placeholder instead.
  const { isSignedIn } = useAuth();
  const { getSettingByKey } = useSettings({ enabled: isSignedIn === true });

  if (value === null) {
    return <>-</>;
  }

  const maximumFractionDigits = FRACTION_DIGITS_BY_UNIT[unit] ?? DEFAULT_FRACTION_DIGITS;

  if (getSettingByKey('unit') === 'imperial') {
    return (
      <>
        {value.toLocaleString(undefined, { maximumFractionDigits })} {unit}
      </>
    );
  }

  switch (unit) {
    case 'mph':
      return <>{convert(value).from('m/h').to('km/h')?.toLocaleString(undefined, { maximumFractionDigits })} kmh</>;

    case 'ft':
      return <>{convert(value).from('ft').to('m')?.toLocaleString(undefined, { maximumFractionDigits })} m</>;

    case 'lb':
      return <>{convert(value).from('lb').to('kg')?.toLocaleString(undefined, { maximumFractionDigits })} kg</>;

    case 'lt':
      return <>{ltToT(value)?.toLocaleString()} t</>;

    case 'sq.ft':
      return <>{convert(value).from('ft2').to('m2')?.toLocaleString(undefined, { maximumFractionDigits })} sq.m</>;

    case 'gallon':
      return <>{convert(value).from('gal').to('l')?.toLocaleString(undefined, { maximumFractionDigits })} liter</>;

    case 'knots':
      return <>{convert(value).from('knot').to('km/h')?.toLocaleString(undefined, { maximumFractionDigits })} kmh</>;

    default:
      return (
        <>
          {value?.toLocaleString(undefined, { maximumFractionDigits })} {unit}
        </>
      );
  }
};

export default UnitDetail;
